import { Injectable } from '@angular/core';
import { AlertController } from '@ionic/angular';

/** Pagina con dati modificabili: dice se ha modifiche non salvate, come chiamarle a video e (se può) le salva. */
export interface UnsavedChangesSource {
  hasUnsavedChanges(): boolean;
  /** Cosa si perderebbe, es. «Quotation Q-1234». */
  label(): string;
  /**
   * Salva con gli stessi controlli del pulsante Salva; true = salvato, false = non salvato (la pagina ha già detto
   * perché all'utente). Senza, al logout si può solo restare o uscire senza salvare.
   */
  save?(): Promise<boolean>;
}

/**
 * ✏️ 2026-11-02 — modifiche non salvate a livello di app, per le uscite che non passano dalla navigazione.
 * La pagina con dati modificabili (oggi l'editor quotazioni, via `QuotationWrapperComponent`) si registra
 * finché è quella attiva; il logout VOLONTARIO (`SessionService.logout`) chiede qui, come il C#
 * `frmQuotazioni_FormClosing` che offre di salvare: «Stay» / «Save and log out» / «Log out without saving».
 * La navigazione resta della guardia di rotta (`quoteUnsavedGuard`), la chiusura della scheda del
 * `beforeunload` dell'editor. Il logout per sessione scaduta (401, `handleUnauthorized`) non passa di qui:
 * la sessione non c'è più e non c'è niente da scegliere.
 */
@Injectable({ providedIn: 'root' })
export class UnsavedChangesService {

  private sources = new Set<UnsavedChangesSource>();
  /** Una sola domanda alla volta: un doppio clic su «Log out» riusa la stessa risposta. */
  private prompt: Promise<boolean> | null = null;

  constructor(private alertCtrl: AlertController) {}

  /** Registra una pagina; restituisce la funzione che la toglie (da chiamare quando la pagina se ne va). */
  register(source: UnsavedChangesSource): () => void {
    this.sources.add(source);
    return () => { this.sources.delete(source); };
  }

  /** Pagine con modifiche non salvate (vuoto = nessuna). */
  private pendingSources(): UnsavedChangesSource[] {
    return [...this.sources].filter(s => s.hasUnsavedChanges());
  }

  /** Etichette delle pagine con modifiche non salvate (vuoto = nessuna). */
  pending(): string[] {
    return this.pendingSources().map(s => s.label());
  }

  /**
   * Prima del logout volontario: true = si può uscire (nessuna modifica, salvate, o abbandonate). Con «Save and log
   * out» si salva pagina per pagina: se un salvataggio non riesce il logout non parte e l'utente resta lì.
   */
  confirmLogout(): Promise<boolean> {
    const pending = this.pendingSources();
    if (pending.length === 0) return Promise.resolve(true);
    if (!this.prompt) {
      this.prompt = (async () => {
        const canSave = pending.every(s => typeof s.save === 'function');
        const alert = await this.alertCtrl.create({
          header: 'Unsaved changes',
          message: `${pending.map(s => s.label()).join('\n')}\n\n`
            + (canSave ? 'These changes have not been saved.' : 'These changes have not been saved. If you log out now they will be lost.'),
          cssClass: 'alert-pre-line',   // a capo veri: ion-alert mostra il messaggio come testo
          buttons: [
            { text: 'Stay', role: 'cancel' },
            ...(canSave ? [{ text: 'Save and log out', role: 'save' }] : []),
            { text: canSave ? 'Log out without saving' : 'Log out', role: 'destructive' },
          ],
        });
        await alert.present();
        const { role } = await alert.onDidDismiss();
        if (role === 'destructive') return true;
        if (role !== 'save') return false;
        for (const s of pending) {
          if (s.hasUnsavedChanges() && !(await s.save!())) return false;
        }
        return true;
      })().finally(() => { this.prompt = null; });
    }
    return this.prompt;
  }
}
